import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { FaqItem } from "../../models/faq";
import FaqService from "../../services/api/faq.service";
import {
  ActionBtn,
  Btn,
  Card,
  DragHandle,
  Input,
  Textarea,
} from "../../style/admin/FaqAdmin_style";
import QuillEditor from "../../components/QuillEditor";
import { nanoid } from "nanoid";
import { toast } from "../../utils/notify";
import TiptapEditor from "../../components/TipTapEditor";
import { envConfig } from "../../config/env.config";

interface FaqProps {
  faqItems: FaqItem[];
  objIdProp: string | null;
}

const EMPTY_GUID = "00000000-0000-0000-0000-000000000000";

/* ---------- Item draggable ---------- */
function SortableFaqItem({
  item,
  index,
  updateFaq,
  cloneFaq,
  removeFaq,
  faqs,
}: any) {
  const isValid =
    item.question?.trim() && item.answer && item.answer !== "<p><br></p>";
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.clientId });

  const style: React.CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <div ref={setNodeRef} style={style}>
      <Card dragging={isDragging}>
        <DragHandle
          {...(isValid ? attributes : {})}
          {...(isValid ? listeners : {})}
          style={{
            opacity: isValid ? 1 : 0.4,
            cursor: isValid ? "grab" : "not-allowed",
          }}>
          ☰
        </DragHandle>

        <ActionBtn onClick={() => cloneFaq(index)}>+</ActionBtn>
        <ActionBtn
          danger
          //disabled={faqs.length === 1}
          onClick={() => removeFaq(index)}>
          ✕
        </ActionBtn>

        <Input
          placeholder={`Câu hỏi #${index + 1}`}
          value={item.question}
          onChange={(e) => updateFaq(index, "question", e.target.value)}
        />

        <TiptapEditor
          height={200}
          value={item.answer}
          onChange={(value: string) => updateFaq(index, "answer", value)}
        />
      </Card>
    </div>
  );
}

interface FaqRef {
  submit: () => Promise<void>;
  validate: () => Promise<boolean>;
}

/* ---------- Main ---------- */
const FaqAdmin = forwardRef<FaqRef, FaqProps>(
  ({ faqItems, objIdProp }, ref) => {
    const [faqs, setFaqs] = useState<FaqItem[]>([]);
    const [objId, setObjId] = useState<string | null>();

    type Language = typeof envConfig.KEY_BASE extends "vn"
      ? "VI" | "EN"
      : "VI" | "EN" | "ID" | "TH";

    const [language, setLanguage] = useState<Language>(
      envConfig.KEY_BASE === "vn" ? "VI" : "EN",
    );

    const isFaqEmpty = (faq: FaqItem) =>
      faq.question.trim() === "" && isEmptyQuill(faq.answer);

    useEffect(() => {
      setObjId(objIdProp);

      // ✅ filter theo tab language
      const filtered = (faqItems || []).filter(
        (x: any) => x.language === language,
      );

      if (!filtered || filtered.length === 0) {
        setFaqs([createEmptyFaq()]);
        return;
      }

      setFaqs(
        [...filtered]
          .sort((a, b) => a.position - b.position)
          .map((x) => ({
            ...x,
            clientId: nanoid(),
          })),
      );
    }, [faqItems, objIdProp, language]);

    const sensors = useSensors(useSensor(PointerSensor));

    const cloneFaq = (index: number) => {
      const newFaq = createEmptyFaq();

      setFaqs((prev) => {
        const next = [...prev];

        // chèn vào vị trí index + 1
        next.splice(index + 1, 0, newFaq);

        return next;
      });
    };
    const updateFaq = (
      index: number,
      field: "question" | "answer",
      value: string,
    ) => {
      const clone = [...faqs];
      clone[index][field] = value;
      setFaqs(clone);
    };

    const onDragEnd = (event: any) => {
      if (hasInvalidFaq(faqs)) {
        alert("Vui lòng nhập đầy đủ trước khi sắp xếp");
        return;
      }

      const { active, over } = event;
      if (!over || active.id === over.id) return;

      setFaqs((prev) => {
        const oldIndex = prev.findIndex((x) => x.clientId === active.id);
        const newIndex = prev.findIndex((x) => x.clientId === over.id);

        const reordered = arrayMove(prev, oldIndex, newIndex);

        // ✅ cập nhật position luôn
        return reordered.map((x, i) => ({
          ...x,
          position: i + 1,
        }));
      });
    };

    const persistFaqs = async (list: FaqItem[]) => {
      await FaqService.createOrUpdate({
        objId,
        faqs: list.map((x) => ({
          id: x.id,
          question: x.question,
          objId,
          answer: x.answer,
          position: x.position,
          language,
        })),
        language,
      });
    };

    useImperativeHandle(ref, () => ({
      submit: async () => {
        await persistFaqs(faqs);
      },

      validate: async () => {
        if (hasInvalidFaq(faqs)) {
          toast("Vui lòng nhập đầy đủ Câu hỏi và Trả lời trong FAQ", "error");
          return false;
        }

        return true;
      },
    }));

    const normalizePosition = (list: FaqItem[]): FaqItem[] =>
      list.map((x, i) => ({
        ...x,
        Position: i + 1,
      }));

    const createEmptyFaq = (): FaqItem => ({
      id: EMPTY_GUID,
      clientId: nanoid(), // 👈 thêm dòng này
      question: "",
      objId: objId ?? "",
      answer: "",
      position: 0,
    });

    const removeFaq = (index: number) => {
      setFaqs((prev) => prev.filter((_, i) => i !== index));
    };

    const isEmptyQuill = (html: string) =>
      !html || html === "<p><br></p>" || html.trim() === "";

    const isFaqValid = (faq: FaqItem) =>
      faq.question.trim() !== "" && !isEmptyQuill(faq.answer);

    const hasInvalidFaq = (list: FaqItem[]) => {
      // ✅ chỉ có 1 FAQ & nó đang hoàn toàn rỗng → OK
      if (list.length === 1 && isFaqEmpty(list[0])) {
        return false;
      }

      // ❌ các trường hợp còn lại → check nghiêm
      return list.some((faq) => !isFaqValid(faq));
    };

    return (
      <div>
        {envConfig.KEY_BASE === "vn" ? (
          <>
            <div style={{ marginBottom: 12 }}>
              <Btn
                type="button"
                onClick={() => setLanguage("VI")}
                style={{
                  backgroundColor: "#2563eb",
                  color: "#ffffff",
                  opacity: language === "VI" ? 1 : 0.6,
                }}>
                🇻🇳 Tiếng Việt
              </Btn>

              <Btn
                type="button"
                onClick={() => setLanguage("EN")}
                style={{
                  backgroundColor: "#2563eb",
                  color: "#ffffff",
                  opacity: language === "EN" ? 1 : 0.6,
                  marginLeft: 8,
                }}>
                🇬🇧 English
              </Btn>
            </div>
          </>
        ) : (
          <>
            <div style={{ marginBottom: 12 }}>
              <Btn
                type="button"
                onClick={() => setLanguage("EN")}
                style={{
                  backgroundColor: "#2563eb",
                  color: "#ffffff",
                  opacity: language === "EN" ? 1 : 0.6,
                }}>
                🇬🇧 English
              </Btn>

              <Btn
                type="button"
                onClick={() => setLanguage("ID")}
                style={{
                  backgroundColor: "#2563eb",
                  color: "#ffffff",
                  opacity: language === "ID" ? 1 : 0.6,
                  marginLeft: 8,
                }}>
                🇮🇩 Indonesia
              </Btn>

              <Btn
                type="button"
                onClick={() => setLanguage("TH")}
                style={{
                  backgroundColor: "#2563eb",
                  color: "#ffffff",
                  opacity: language === "TH" ? 1 : 0.6,
                  marginLeft: 8,
                }}>
                🇹🇭 Thai
              </Btn>
            </div>
          </>
        )}

        {faqs.length > 0 && (
          <>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={hasInvalidFaq(faqs) ? undefined : onDragEnd}>
              <SortableContext
                items={faqs.map((x) => x.clientId)}
                strategy={verticalListSortingStrategy}>
                {faqs.map((item, index) => (
                  <SortableFaqItem
                    key={item.clientId}
                    item={item}
                    index={index}
                    updateFaq={updateFaq}
                    cloneFaq={cloneFaq}
                    removeFaq={removeFaq}
                    faqs={faqs}
                  />
                ))}
              </SortableContext>
            </DndContext>
          </>
        )}
      </div>
    );
  },
);

export default FaqAdmin;
